import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Video, Play } from 'lucide-react';
import { getVideos, createVideo, updateVideo, deleteVideo } from '../services/adminApi';
import FileUpload from '../components/FileUpload';

const VideosManager = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [formData, setFormData] = useState({
    title: '',
    url: '',
    type: 'hero',
    locationId: '',
    isActive: true
  });

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const data = await getVideos();
      setVideos(data);
    } catch (error) {
      console.error('Failed to fetch videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate URL is present
    if (!formData.url || formData.url.trim() === '') {
      alert('Please upload a video file before submitting');
      return;
    }
    
    try {
      if (editingVideo) {
        await updateVideo(editingVideo._id, formData);
      } else {
        await createVideo(formData);
      }
      fetchVideos();
      resetForm();
    } catch (error) {
      console.error('Failed to save video:', error);
      alert('Failed to save video: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleEdit = (video) => {
    setEditingVideo(video);
    setFormData({
      title: video.title,
      url: video.url,
      type: video.type,
      locationId: video.locationId || '',
      isActive: video.isActive
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this video?')) {
      try {
        await deleteVideo(id);
        fetchVideos();
      } catch (error) {
        console.error('Failed to delete video:', error);
        alert('Failed to delete video');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      url: '',
      type: 'hero',
      locationId: '',
      isActive: true
    });
    setEditingVideo(null);
    setShowModal(false);
  };

  if (loading) {
    return <div className="text-white text-center py-12">Loading videos...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-black text-white mb-2">Videos Manager</h1>
          <p className="text-gray-400">Manage all website videos: homepage, locations, jump diaries, real stories & influencer videos</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center space-x-2 bg-yellow-400 hover:bg-yellow-300 text-black font-bold px-6 py-3 rounded-lg transition-colors"
        >
          <Plus size={20} />
          <span>New Video</span>
        </button>
      </div>

      {/* Filter */}
      <div className="flex gap-4 items-center">
        <label className="text-white font-medium">Filter by Type:</label>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-400"
        >
          <option value="all">All Videos</option>
          <option value="hero">Hero (Homepage)</option>
          <option value="location">Location Videos</option>
          <option value="diary">Jump Diaries</option>
          <option value="testimonial">Real Stories (Testimonials)</option>
          <option value="influencer">Influencer Videos</option>
          <option value="new-era">New Era Videos</option>
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {videos.filter(video => filterType === 'all' || video.type === filterType).map((video) => (
          <div key={video._id} className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden hover:border-yellow-400 transition-colors">
            <div className="w-full h-48 bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center">
              <Play className="text-yellow-400" size={64} />
            </div>
            <div className="p-6">
              <div className="flex items-center justify-between mb-3">
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${video.isActive ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                  {video.isActive ? 'Active' : 'Inactive'}
                </span>
                <span className={`px-3 py-1 rounded-full text-xs font-bold capitalize ${
                  video.type === 'hero' ? 'bg-yellow-500/20 text-yellow-400' :
                  video.type === 'diary' ? 'bg-purple-500/20 text-purple-400' :
                  video.type === 'testimonial' ? 'bg-green-500/20 text-green-400' :
                  video.type === 'influencer' ? 'bg-pink-500/20 text-pink-400' :
                  video.type === 'new-era' ? 'bg-orange-500/20 text-orange-400' :
                  'bg-blue-500/20 text-blue-400'
                }`}>
                  {video.type === 'diary' ? 'Jump Diary' : 
                   video.type === 'testimonial' ? 'Real Stories' :
                   video.type === 'influencer' ? 'Influencer' :
                   video.type === 'new-era' ? 'New Era' :
                   video.type}
                </span>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">{video.title}</h3>
              <p className="text-gray-400 text-sm mb-4 truncate">{video.url}</p>
              <div className="flex space-x-2">
                <button onClick={() => handleEdit(video)} className="flex-1 p-2 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg text-blue-400 transition-colors">
                  <Edit2 size={16} className="mx-auto" />
                </button>
                <button onClick={() => handleDelete(video._id)} className="flex-1 p-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-red-400 transition-colors">
                  <Trash2 size={16} className="mx-auto" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {videos.length === 0 && (
        <div className="text-center py-12">
          <Video className="mx-auto mb-4 text-gray-600" size={48} />
          <p className="text-gray-400">No videos found</p>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-gray-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col my-8">
            <div className="p-6 border-b border-gray-700 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-white">{editingVideo ? 'Edit Video' : 'Add New Video'}</h2>
              <button
                type="button"
                onClick={resetForm}
                className="p-2 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="text-white font-medium mb-2 block">Title *</label>
                <input type="text" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400" />
              </div>
              <FileUpload
                value={formData.url}
                onChange={(url) => {
                  console.log('Video URL updated:', url);
                  setFormData({ ...formData, url });
                }}
                type="videos"
                accept="video/*"
                label="Video *"
              />
              {formData.url && (
                <div className="text-green-400 text-sm mt-2">
                  ✓ Video uploaded successfully
                </div>
              )}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-white font-medium mb-2 block">Type *</label>
                  <select value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })} className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400">
                    <option value="hero">Hero (Homepage Video)</option>
                    <option value="location">Location Video</option>
                    <option value="gallery">Gallery Video</option>
                    <option value="diary">Jump Diary</option>
                    <option value="testimonial">Real Stories (Testimonial)</option>
                    <option value="influencer">Influencer Video</option>
                    <option value="new-era">New Era Video</option>
                  </select>
                </div>
                <div>
                  <label className="text-white font-medium mb-2 block">Location ID (if applicable)</label>
                  <input type="text" value={formData.locationId} onChange={(e) => setFormData({ ...formData, locationId: e.target.value })} className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400" />
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <input type="checkbox" id="isActive" checked={formData.isActive} onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })} className="w-5 h-5 bg-gray-700 border-gray-600 rounded" />
                <label htmlFor="isActive" className="text-white font-medium">Active</label>
              </div>
              <div className="flex justify-end space-x-4 pt-4">
                <button type="button" onClick={resetForm} className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors">Cancel</button>
                <button type="submit" className="px-6 py-3 bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-lg transition-colors">{editingVideo ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideosManager;